// src/App.jsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { SessionContextProvider, useSessionContext } from '@supabase/auth-helpers-react'
import { supabase } from './supabaseClient'
import { Toaster } from 'react-hot-toast'
import { SidebarProvider } from './contexts/SidebarContext'
import { UserProvider } from './contexts/UserContext'
import ProtectedLayout from './components/ProtectedLayout'
// import ChatBox from './components/ChatBox'
// import NotificationBell from './components/NotificationBell'

import Home from './pages/Home'
import LandingPage from './pages/LandingPage'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import Profile from './pages/Profile'
import EditProfile from './pages/EditProfile'
import ManageUsers from './pages/ManageUsers'
import AddProject from './pages/AddProject'
import EditProject from './pages/EditProject'
import CVPreview from './pages/CVPreview'
import Timesheet from './pages/Timesheet'
import LeaveRequest from './pages/LeaveRequest'
import LeavePermitForm from './pages/LeavePermitForm'
import JobApplication from './pages/JobApplication'
import ResignationLetter from './pages/ResignationLetter'
import SplitBill from './pages/SplitBill'
import Attendance from './pages/Attendance'
import TaskManagement from './pages/TaskManagement'

function AppRoutes() {
  const { session, isLoading } = useSessionContext()
  const [userRole, setUserRole] = useState(null)
  const [loadingTimeout, setLoadingTimeout] = useState(false)

  const user = session?.user

  // Timeout handler untuk loading yang stuck
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true)
      }, 5000) // 5 detik timeout

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single()

        if (!error && data) {
          setUserRole(data.role)
        }
      }
    }

    fetchUserRole()
  }, [user])

  if (isLoading && !loadingTimeout) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  // Jika timeout, tampilkan pesan error dan redirect ke home
  if (loadingTimeout) {
    window.location.href = '/'
    return null
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes - No Sidebar/Navbar */}
        <Route path="/" element={<Home />} />
        <Route path="/hr-management" element={<LandingPage />} />
        <Route
          path="/login"
          element={!user ? <Login /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/register"
          element={!user ? <Register /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/forgot-password"
          element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        
        {/* Protected Routes - With Sidebar/Navbar */}
        <Route
          path="/dashboard"
          element={user ? <ProtectedLayout><Dashboard /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/profile" 
          element={user ? <ProtectedLayout><Profile /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/edit-profile" 
          element={user ? <ProtectedLayout><EditProfile /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/add-project" 
          element={user ? <ProtectedLayout><AddProject /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/edit-project/:id" 
          element={user ? <ProtectedLayout><EditProject /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/cv-preview" 
          element={user ? <ProtectedLayout><CVPreview /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/timesheet" 
          element={user ? <ProtectedLayout><Timesheet /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/leave-request" 
          element={user ? <ProtectedLayout><LeaveRequest /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/leave-permit-form" 
          element={user ? <ProtectedLayout><LeavePermitForm /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/job-application" 
          element={user ? <ProtectedLayout><JobApplication /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/resignation-letter" 
          element={user ? <ProtectedLayout><ResignationLetter /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/split-bill" 
          element={user ? <ProtectedLayout><SplitBill /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/attendance" 
          element={user ? <ProtectedLayout><Attendance /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route 
          path="/tasks" 
          element={user ? <ProtectedLayout><TaskManagement /></ProtectedLayout> : <Navigate to="/login" replace />}
        />
        <Route
          path="/admin/users"
          element={
            user && userRole === 'admin' ? (
              <ProtectedLayout><ManageUsers /></ProtectedLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>

      {/* Chat Box - Show only when user is logged in */}
      {/* Temporarily hidden for future development */}
      {/* {user && <ChatBox />} */}

      {/* ✅ Toaster */}
      <Toaster position="top-right" reverseOrder={false} />
    </BrowserRouter>
  )
}

// ✅ Bungkus App dengan SessionContextProvider, UserProvider, dan SidebarProvider
export default function App() {
  return (
    <SessionContextProvider 
      supabaseClient={supabase}
      initialSession={null}
    >
      <UserProvider>
        <SidebarProvider>
          <AppRoutes />
        </SidebarProvider>
      </UserProvider>
    </SessionContextProvider>
  )
}
